import { memo, useState } from 'react'
import { X, ChevronDown, ChevronRight } from 'lucide-react'
import { LegalDialog } from './LegalDialog'
import { TosContent, PrivacyContent } from './LegalContent'

interface Props {
  open: boolean
  onClose: () => void
}

interface Faq { q: string; a: string }

const FAQ: Faq[] = [
  {
    q: 'What is PolyStrike Visual?',
    a: 'A visual terminal for trading on Polymarket: BTC / ETH / SOL price-range matrix × 3 days overlaid on a live price chart. One tap on a cell — a limit YES / NO order via Polymarket CLOB. Long-press selects cells, column arbitrage sums are shown on top.',
  },
  {
    q: 'How does the wallet work?',
    a: 'Non-custodial. Your PM wallet private key (exported from polymarket.com) is encrypted with your PIN via PBKDF2-SHA256 (200,000 iterations) + AES-256-GCM and stored only in this browser\u2019s localStorage. The server never sees the key.',
  },
  {
    q: 'How does the subscription work?',
    a: 'The first 7 days after connecting the PM wallet are a free trial with full functionality. Then 8.5 USDC / 30 days. Payment: you sign an on-chain transfer from your own PM wallet to treasury yourself. The watcher sees the transaction and activates the subscription. The key never leaves the browser.',
  },
  {
    q: 'What happens when the subscription expires?',
    a: 'The matrix and chart remain visible (viewing is free), but placing new orders is blocked. You can manage open positions and redeem winnings on polymarket.com — log in with the same PM wallet.',
  },
  {
    q: 'Why is a PIN required?',
    a: 'The PIN is the password to the AES cipher protecting your private key. Without the PIN, the key cannot be decrypted even with physical access to the device.',
  },
  {
    q: 'What is the arbitrage highlight?',
    a: 'A long tap (1s) selects cells. The sum of YES prices for the selection is shown above the column. If the sum is < 100¢ — green — buying all these ranges is guaranteed to win more than spent.',
  },
  {
    q: 'Is there Auto-Redeem?',
    a: 'No — in Visual, redeem is manual: the button activates once a position has resolved as a winner. Gasless, fully client-side (via Polymarket Builder Relayer).',
  },
]

function FaqItem({ item }: { item: Faq }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-lg border border-[#374151] bg-[#0d1117]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-2 px-3 py-2 text-left"
      >
        {open ? (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-[#94a3b8]" />
        ) : (
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-[#94a3b8]" />
        )}
        <span className="text-sm text-white">{item.q}</span>
      </button>
      {open && <div className="px-3 pb-3 pl-9 text-xs leading-relaxed text-[#94a3b8]">{item.a}</div>}
    </div>
  )
}

export const ReadmeDialog = memo(function ReadmeDialog({ open, onClose }: Props) {
  const [legal, setLegal] = useState<'tos' | 'privacy' | null>(null)

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="readme-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close"
      />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-[#374151] bg-[#111827] p-5 shadow-xl max-h-[90dvh] overflow-y-auto">
        <div className="mb-4 flex items-center justify-between">
          <h2 id="readme-title" className="text-lg font-semibold text-white">About PolyStrike Visual</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[#94a3b8] hover:bg-white/10 hover:text-white"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tier */}
        <section className="mb-5">
          <div className="rounded-lg border border-[#374151] bg-[#1f2937] px-3 py-3">
            <div className="mb-1 flex items-baseline justify-between">
              <span className="text-sm font-semibold text-[#22c55e]">PolyStrike Visual</span>
              <span className="text-xs text-[#94a3b8]">8.5 USDC / 30 days</span>
            </div>
            <div className="mb-2 text-[11px] text-[#f59e0b]">First 7 days — free trial</div>
            <ul className="space-y-1 text-xs text-[#e2e8f0]">
              <li className="flex items-start gap-1.5"><span className="mt-0.5 text-[#22c55e]">+</span><span>BTC / ETH / SOL price-range matrix × 3 days</span></li>
              <li className="flex items-start gap-1.5"><span className="mt-0.5 text-[#22c55e]">+</span><span>Live price + 3-day chart</span></li>
              <li className="flex items-start gap-1.5"><span className="mt-0.5 text-[#22c55e]">+</span><span>Limit BUY / SELL via Polymarket CLOB</span></li>
              <li className="flex items-start gap-1.5"><span className="mt-0.5 text-[#22c55e]">+</span><span>Gasless winnings redeem</span></li>
              <li className="flex items-start gap-1.5"><span className="mt-0.5 text-[#22c55e]">+</span><span>Non-custodial: key never leaves the browser</span></li>
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#94a3b8]">FAQ</h3>
          <div className="space-y-1.5">
            {FAQ.map((item, i) => <FaqItem key={i} item={item} />)}
          </div>
        </section>

        {/* Disclaimer */}
        <section className="mb-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#94a3b8]">Risks</h3>
          <div className="rounded-lg border border-[#f59e0b]/30 bg-[#f59e0b]/10 px-3 py-2.5 text-[11px] leading-relaxed text-[#f59e0b]">
            PolyStrike Visual is an interface to the Polymarket CLOB. Trading predictions
            involves the risk of total loss of funds. Non-custodial: the application does
            not control your funds and does not reimburse losses. You are fully responsible
            for your decisions. Check that Polymarket is available in your jurisdiction.
          </div>
        </section>

        {/* Legal + Support */}
        <section className="mb-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#94a3b8]">Support &amp; Legal</h3>
          <div className="space-y-1.5 text-xs">
            <div className="flex items-center justify-between rounded-lg bg-[#1f2937] px-3 py-2">
              <span className="text-[#94a3b8]">Email</span>
              <a href="mailto:support@polystrike.app" className="text-[#22c55e] hover:text-[#16a34a]">
                support@polystrike.app
              </a>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-[#1f2937] px-3 py-2">
              <span className="text-[#94a3b8]">Telegram</span>
              <a
                href="https://t.me/PolyStrike_autobot"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#22c55e] hover:text-[#16a34a]"
              >
                @PolyStrike_autobot
              </a>
            </div>
            <button
              type="button"
              onClick={() => setLegal('tos')}
              className="flex w-full items-center justify-between rounded-lg bg-[#1f2937] px-3 py-2 text-left hover:bg-[#374151]"
            >
              <span className="text-[#94a3b8]">Terms of Service</span>
              <span className="text-[#22c55e]">Open</span>
            </button>
            <button
              type="button"
              onClick={() => setLegal('privacy')}
              className="flex w-full items-center justify-between rounded-lg bg-[#1f2937] px-3 py-2 text-left hover:bg-[#374151]"
            >
              <span className="text-[#94a3b8]">Privacy Policy</span>
              <span className="text-[#22c55e]">Open</span>
            </button>
          </div>
          <div className="mt-2 text-[10px] text-[#64748b]">
            Support response SLA: 24 hours on business days.
          </div>
        </section>

        <div className="mt-5 text-center text-[10px] text-[#64748b]">
          PolyStrike Visual · non-custodial · AES-256-GCM + PBKDF2-200k
        </div>
      </div>

      <LegalDialog open={legal === 'tos'} title="Terms of Service" onClose={() => setLegal(null)}>
        <TosContent />
      </LegalDialog>
      <LegalDialog open={legal === 'privacy'} title="Privacy Policy" onClose={() => setLegal(null)}>
        <PrivacyContent />
      </LegalDialog>
    </div>
  )
})
